// "Run this in k0-lang →" deep links: a URL fragment carrying SAS to
// prefill (and optionally autorun) the editor, plus the inverse —
// building a shareable link from the current buffer for the toolbar's
// copy action.
//
// The payload rides the URL *fragment*, never the query string. Like
// the auth token (see auth.ts / SEC-011), fragments aren't sent to the
// server, so the SAS never lands in Cloudflare/CDN access logs — which
// keeps it consistent with the telemetry rule "never send source code".
//
// Ordering note: this MUST be read before `captureTokenFromUrl`, which
// drops the *entire* fragment. We strip only our own keys (`code`,
// `run`) and rebuild the hash so a fragment carrying both a token and a
// `#code` (unusual, but possible across an auth round-trip) leaves the
// token intact for auth to consume next.
//
// The editor needs an account, and a sign-in leaves the page (OAuth) or
// lands in a new tab (magic link). `stashDeepLink` keeps a link opened
// while signed out for one hour; `takeStashedDeepLink` gives it back
// to the signed-in boot that follows.

const CODE_PARAM = 'code';
const RUN_PARAM = 'run';
const STASH_KEY = 'kolistat.deep_link.pending';
const STASH_TTL_MS = 60 * 60 * 1000;

// A program longer than this won't survive as a URL across mail
// clients / chat apps / address bars (the de-facto safe ceiling sits
// around 8 KB). We still let the caller copy, but warn first.
export const SHARE_URL_MAX = 8000;

export interface DeepLink {
  /// Decoded SAS to prefill into the scratch tab.
  code: string;
  /// Whether to autorun after the engine is ready (`run=1`).
  run: boolean;
}

/// Inspect the URL fragment for `#code=<encoded sas>[&run=1]`. If
/// present, decode it, strip ONLY the `code`/`run` keys from the
/// fragment (preserving any other fragment params, e.g. an auth
/// `#token`), and return the parsed deep link. Returns null when no
/// `code` param is present.
export function captureDeepLinkFromUrl(): DeepLink | null {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const raw = params.get(CODE_PARAM);
  if (raw === null) return null;

  let code: string;
  try {
    code = decodeURIComponent(raw);
  } catch {
    // Malformed percent-encoding — treat the link as absent rather than
    // prefilling garbage. Still strip the keys below so a reload doesn't
    // keep retrying.
    code = '';
  }
  const run = params.get(RUN_PARAM) === '1';

  // Remove only our keys; whatever's left (a token, future params)
  // stays in the fragment for the next consumer.
  params.delete(CODE_PARAM);
  params.delete(RUN_PARAM);
  const rest = params.toString();
  const clean =
    window.location.pathname +
    window.location.search +
    (rest ? `#${rest}` : '');
  window.history.replaceState({}, '', clean);

  return code ? { code, run } : null;
}

/// Build a shareable link that prefills + autoruns `source`. Mirrors
/// duckdb.ts's `origin` + `BASE_URL` join so it points at the same
/// deployment the user is on (root in prod, subpath in dev configs).
export function buildShareUrl(source: string): string {
  const base = `${window.location.origin}${import.meta.env.BASE_URL}`;
  return `${base}#${CODE_PARAM}=${encodeURIComponent(source)}&${RUN_PARAM}=1`;
}

/// The subset of `Storage` the stash uses, so tests can pass a fake.
export type DeepLinkStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/// Keep `link` for a signed-out visitor. `now` is a millisecond
/// timestamp (`Date.now()`); the entry is good for one hour from it.
/// A storage failure (private mode, quota) is logged and ignored.
export function stashDeepLink(link: DeepLink, storage: DeepLinkStorage, now: number): void {
  try {
    storage.setItem(STASH_KEY, JSON.stringify({ code: link.code, run: link.run, at: now }));
  } catch (e) {
    console.warn('[kolistat deep-link] storage write failed', e);
  }
}

/// Take the kept link. Returns null when there is none, when it is
/// older than one hour at `now`, or when the entry is malformed. The
/// entry is removed in every case, so a link is applied at most once.
export function takeStashedDeepLink(storage: DeepLinkStorage, now: number): DeepLink | null {
  let raw: string | null;
  try {
    raw = storage.getItem(STASH_KEY);
    if (raw === null) return null;
    storage.removeItem(STASH_KEY);
  } catch (e) {
    console.warn('[kolistat deep-link] storage read failed', e);
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { code, run, at } = parsed as Record<string, unknown>;
  if (typeof code !== 'string' || code === '' || typeof at !== 'number') return null;
  if (now - at > STASH_TTL_MS) return null;
  return { code, run: run === true };
}
